import React, { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Loader,
  Paper,
  PasswordInput,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconExclamationCircle } from '@tabler/icons-react';
import { ReactComponent as DeniedSvg } from 'assets/svg/denied.svg';
import { ReactComponent as KiloLogo } from 'assets/svg/long_kilo_blue_white.svg';
import { ReactComponent as VerifySvg } from 'assets/svg/verify.svg';
import useActivateUser from 'hooks/auth/useActivateUser';
import useActivationState from 'hooks/auth/useActivationState';
import { Link, useParams } from 'react-router-dom';
import { ActivationState } from 'types';
import { rePasswordValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';

interface FormValues {
  password: string;
  rePassword: string;
  surname?: string;
}

type State = ActivationState['state'] | 'loading' | 'error';

const Activate = (): JSX.Element => {
  const theme = useMantineTheme();
  const { uid, token } = useParams();

  const [state, setState] = useState<State>('loading');

  const activationStateMutation = useActivationState();
  const activateUserMutation = useActivateUser();

  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      password: '',
      rePassword: '',
      surname: '',
    },

    validate: {
      password: value => (value ? null : 'Required'),
      rePassword: (value, values) => rePasswordValidator(values.password)(value),
    },
  });

  const handleSubmit = useCallback(
    (formData: FormValues): void => {
      if (uid && token) {
        const request = { uid, token, ...formData };
        activateUserMutation.mutate(request, {
          onError: error => {
            errorNotification('Activation failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Activation successful', 'Your account has been activated.');
            setState('active');
          },
        });
      }
    },
    [uid, token, activateUserMutation, errorNotification, successNotification],
  );

  useEffect(() => {
    activationStateMutation.mutate(
      { uid: uid || '', token: token || '' },
      {
        onSuccess: response => {
          setState(response.data.state);
        },
        onError: () => {
          setState('error');
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'loading') {
    return (
      <Container size='xs' py='xl'>
        <Box>
          <KiloLogo width={100} height='100%' />
        </Box>
        <Center my='xl' sx={{ height: '200px' }}>
          <Loader variant='bars' size='xl' />
        </Center>
      </Container>
    );
  }

  if (state === 'error') {
    return (
      <Container size='xs' py='xl'>
        <Box>
          <KiloLogo width={100} height='100%' />
        </Box>
        <Center my='xl' sx={{ height: '200px' }}>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong while fetching data!
          </Alert>
        </Center>
      </Container>
    );
  }

  if (state === 'invalid' || state === 'expired') {
    return (
      <Container size='xs' py='xl'>
        <Box>
          <KiloLogo width={100} height='100%' />
        </Box>
        <Container mt='xl' fluid>
          <DeniedSvg width='100%' height={200} />
        </Container>
        <Paper withBorder shadow='md' p={30} mt={30} radius='md'>
          {state === 'invalid' && (
            <Text color='dimmed'>This link is invalid, click button to go back to login page.</Text>
          )}
          {state === 'expired' && (
            <Text color='dimmed'>This link is expired, click button to go back to login page.</Text>
          )}
          <Button
            component={Link}
            to='/login'
            mt='xl'
            leftIcon={<IconChevronLeft size={theme.fontSizes.lg} />}
            fullWidth
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (state === 'active') {
    return (
      <Container size='xs' py='xl'>
        <KiloLogo width={100} height='100%' />
        <Container mt='xl' fluid>
          <VerifySvg width='100%' height={200} />
        </Container>
        <Title align='center' mt='xl' sx={{ fontWeight: 900 }}>
          Account Activated
        </Title>
        <Paper withBorder shadow='md' p={30} mt={30} radius='md'>
          <Text color='dimmed'>Please use the link below to login to Kilo.</Text>
          <Button
            component={Link}
            to='/login'
            mt='xl'
            leftIcon={<IconChevronLeft size={theme.fontSizes.lg} />}
            fullWidth
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (state === 'inactive') {
    return (
      <Container size='xs' py='xl'>
        <KiloLogo width={100} height='100%' />
        <Container mt='xl' fluid>
          <VerifySvg width='100%' height={200} />
        </Container>
        <Title align='center' mt='xl' sx={{ fontWeight: 900 }}>
          Activate your Account
        </Title>

        <Paper withBorder shadow='md' p={30} mt={30} radius='md'>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <PasswordInput
              label='Password'
              placeholder='•••••••'
              required
              mt='md'
              {...form.getInputProps('password')}
            />
            <PasswordInput
              label='Re-password'
              placeholder='•••••••'
              required
              mt='md'
              {...form.getInputProps('rePassword')}
            />
            <TextInput mt='md' type='hidden' {...form.getInputProps('surname')} />
            <Button fullWidth mt='xl' type='submit' loading={activateUserMutation.isLoading}>
              Activate
            </Button>
            {/* <Flex gap='md'>
              <Button
                fullWidth
                variant='outline'
                color='gray'
                mt='md'
                onClick={() => authenticateGoogle(true)}
              >
                Google Login
              </Button>
              <Button
                fullWidth
                variant='outline'
                color='gray'
                mt='md'
                onClick={() => authenticateFacebook(true)}
              >
                Facebook Login
              </Button>
              <Button
                fullWidth
                variant='outline'
                color='gray'
                mt='md'
                onClick={() => authenticateApple(true)}
              >
                Apple Login
              </Button>
            </Flex> */}
          </form>
        </Paper>
      </Container>
    );
  }

  return <></>;
};

export default Activate;
